import Channel from '@@types/Channel';
import { MEMBER_ID } from '@@types/common';
import IWebsocketMessageHandler from '@@types/IWebsocketMessageHandler';
import Log from '@@types/Log';
import { ChannelMemberReadSeqUpdateData } from '@@types/websocket/Member';

import { MAIN_THREAD_CHANNEL_ID, MemberStatus, MODAL_NAMES } from '~/constants';
import { SECOND } from '~/constants/date';
import { WebsocketMemberStatusAction } from '~/constants/websocket';
import GA from '~/services/GA/GA';
import WebsocketConnector from '~/services/WebsocketConnector';
import InterTenantMemberMessageHandler from '~/stores/actions/websocketMessageHandlers/InterTenantMember';
import { mainDetailUiState, threadDetailUiState } from '~/stores/DetailUiState';
import { globalSettingStore } from '~/stores/GlobalSettingStore';
import { interTenantMembersStore } from '~/stores/InterTenantMembersStore';
import { membersStore } from '~/stores/MembersStore';
import { api } from '~/stores/providers/Api2';
import { threadChannelStore } from '~/stores/ThreadChannelStore';
import { uiState } from '~/stores/UiState';
import { userStore } from '~/stores/UserStore';
import WebsocketStore, { interTenantWebsocketStore, websocketStore } from '~/stores/WebsocketStore';
import { getHost, getWebsocketProtocol, isMainView } from '~/utils/runEnvironmentUtil';
import { safeParseJsonText } from '~/utils/textUtil/textUtil';
import { isArray, isEmpty, startCase, throttle } from 'lodash';
import { action, IReactionDisposer, reaction } from 'mobx';
import { chain, juxt, tap } from 'ramda';
import __ from 'ramda/es/__';
import always from 'ramda/es/always';
import assoc from 'ramda/es/assoc';
import curry from 'ramda/es/curry';
import equals from 'ramda/es/equals';
import map from 'ramda/es/map';
import pipe from 'ramda/es/pipe';
import replace from 'ramda/es/replace';
import when from 'ramda/es/when';
import { Maybe } from 'ramda-fantasy';

import Actions from '../Actions';
import logging from '../providers/logging';
import { stores } from '../Stores';
import ChannelMessageHandler from './websocketMessageHandlers/Channel';
import CommonMessageHandler from './websocketMessageHandlers/Common';
import FavoriteMessageHandler from './websocketMessageHandlers/Favorite';
import LogMessageHandler from './websocketMessageHandlers/Log';
import MemberMessageHandler from './websocketMessageHandlers/Member';
import NoticeMessageHandler from './websocketMessageHandlers/Notice';
import ReactionMessageHandler from './websocketMessageHandlers/Reaction';
import ThreadChannelMessageHandler from './websocketMessageHandlers/ThreadChannel';

export enum WebsocketHandlerType {
  Default = 'default',
  InterTenant = 'interTenant',
}

class WebsocketActions implements IWebsocketMessageHandler {
  channel: ChannelMessageHandler;
  common: CommonMessageHandler;
  favorite: FavoriteMessageHandler;
  log: LogMessageHandler;
  member: MemberMessageHandler;
  notice: NoticeMessageHandler;
  ws: WebsocketConnector;
  websocketStore: WebsocketStore;
  threadChannel: ThreadChannelMessageHandler;
  reaction: ReactionMessageHandler;

  public sendMemberStatus = curry((action: WebsocketMemberStatusAction, membersId: MEMBER_ID[] | MEMBER_ID) => {
    this.ws.send({
      type: 'memberStatus',
      action,
      content: isArray(membersId) ? membersId : [membersId],
    });
  });

  private interTenantReconnectObserver: IReactionDisposer;
  private sendToNative = curry((userId, message: { type: string; data: any }) => {
    if (message.type === 'memberStatusLocal') {
      this.actions.nativeActions.sendStatusChange({
        memberId: userId,
        memberStatus: message?.data,
      });
    } else if (message.type === 'memberUpdate' && message?.data?.memberId === userId) {
      this.actions.nativeActions.sendLocaleChanged({
        memberId: message?.data.memberId,
        locale: message?.data.locale,
      });
    }
  });

  constructor(public type: WebsocketHandlerType, private actions: Actions) {
    this.ws = new WebsocketConnector(this);
    this.actions.nativeActions.onSuspend(this.handleSuspend);
    this.actions.nativeActions.onResume(this.handleResume);
    this.websocketStore = type === WebsocketHandlerType.Default ? websocketStore : interTenantWebsocketStore;

    this.channel = new ChannelMessageHandler(this.ws, this.actions);
    this.common = new CommonMessageHandler(this.ws, this.actions);
    this.favorite = new FavoriteMessageHandler();
    this.log = new LogMessageHandler(this.ws, this.actions);
    this.member =
      type === WebsocketHandlerType.Default
        ? new MemberMessageHandler(this.ws, this.actions)
        : new InterTenantMemberMessageHandler(this.ws, this.actions);
    this.notice = new NoticeMessageHandler();
    this.threadChannel = new ThreadChannelMessageHandler(this.actions);
    this.reaction = new ReactionMessageHandler();
  }

  private get memberStatusRequest() {
    return this.type === WebsocketHandlerType.Default
      ? membersStore.memberStatusRequest
      : interTenantMembersStore.memberStatusRequest;
  }

  private get isSessionOut() {
    return uiState.isSessionOut;
  }

  public connect = (): Promise<void> => {
    const { accessToken, websocketUrl } = userStore;
    return this.ws.connect(
      this.type === WebsocketHandlerType.Default
        ? `${getWebsocketProtocol()}://${getHost()}${websocketUrl}?token=${accessToken}`
        : `${getWebsocketProtocol()}://${getHost()}/messenger/v4/ws?token=${accessToken}`
    );
  };

  public hasSocket = (): boolean => {
    return this.ws.hasSocket();
  };

  public close = (stopRetry = false) => {
    this.ws.close(stopRetry);
  };

  public clearPingCount = () => {
    this.ws.clearPingCount();
  };

  public getMemberStatus = (membersId: MEMBER_ID | MEMBER_ID[]) => {
    this.sendMemberStatus(WebsocketMemberStatusAction.PEEK, membersId);
  };

  public unsubscribeMemberStatus = (memberId: MEMBER_ID) => {
    this.sendMemberStatus(WebsocketMemberStatusAction.UNSUBSCRIBE, memberId);
  };

  public subscribeMemberStatus = (memberId: MEMBER_ID) => {
    this.sendMemberStatus(WebsocketMemberStatusAction.SUBSCRIBE, memberId);
  };

  public updateStatus = (memberStatus: MemberStatus) => {
    pipe<any, any, any, any, any, any>(
      when(equals(MemberStatus.AutoActive), always(MemberStatus.Active)),
      assoc<any, any>('content', __, { type: 'memberStatus', action: 'local' }),
      // 서버에서 응답이 오기전에 자신의 상태를 native와 sync
      tap(pipe(this.parseToTypeDataShape, this.sendMessageToNative)),
      this.cacheMemberStatusRequest,
      this.ws.send
    )(memberStatus);
  };

  public handleSuspend = () => {
    logging.log('ELECTRON_CHANNEL.MAIN_VIEW.SUSPEND');
    this.close(true);
  };

  public handleResume = () => {
    logging.log('ELECTRON_CHANNEL.MAIN_VIEW.RESUME');
    if (this.type === WebsocketHandlerType.Default) {
      uiState.reload(true);
    }
  };

  public onOpen = () => {
    if (this.type === WebsocketHandlerType.InterTenant) {
      this.interTenantReconnectObserver?.();
      uiState.setIsInterTenantWebsocketOn(true);
    } else if (this.type === WebsocketHandlerType.Default) {
      uiState.setIsWebsocketOn(true);
    }
    this.memberStatusRequest && this.ws.send(this.memberStatusRequest);
  };

  public onClose = (id: string) => {
    if (id === this.ws.socketId) {
      this.ws.resetSocketId();
      this.executeReconnectJob();
    } else {
      GA.writeErrorWebsocket('close id changed');
    }
  };

  public onError = (e: Error, id: string) => {
    if (id === this.ws.socketId) {
      this.ws.resetSocketId();
      GA.writeCloseWebsocket('ERROR');
      GA.writeErrorWebsocket(e.message);
      this.executeReconnectJob();
    } else {
      GA.writeErrorWebsocket('error id changed');
    }
  };

  public handleMessage = (data) => {
    pipe<any, any, any, any, any>(
      safeParseJsonText,
      chain(this.filterVersion),
      map(this.parseToTypeDataShape),
      map(this.handleParsedMessage)
    )(data);
  };

  private filterVersion = (obj) => {
    if (obj.version) {
      return Maybe.Nothing();
    }
    return Maybe.Just(obj);
  };

  public startReconnect = (reconnect: () => Promise<void>) => {
    if (this.websocketStore.reconnectStartTime > 0 || this.websocketStore.reconnectIntervalId) {
      return;
    }
    const startTime = new Date().getTime();
    this.websocketStore.setReconnectStartTime(startTime);
    this.websocketStore.setNextReconnectTime();
    this.websocketStore.startReconnectInterval(() => {
      this.attemptReconnect(reconnect);
    });
  };

  public reconnectWebsocket = () => {
    return this.connect().then(
      juxt([
        () => this.actions.memberActions.sendLocalStatus(this),
        () => {
          if (Date.now() - uiState.getCurrentReconnectTime() > 30 * SECOND) {
            GA.writeReconnectWebsocket('true');
            this.actions.proxyActions.fetchAndSetChannels();
            // 가장 최신 웹소켓 재접속으로 채널 목록 갱신호출 시간 저장
            uiState.setCurrentReconnectTime(Date.now());
          } else {
            GA.writeReconnectWebsocket('false');
          }
          globalSettingStore.fetchDoorayNewsSetting();
          const mainChannelId = mainDetailUiState.contentsChannelId;
          if (mainChannelId === MAIN_THREAD_CHANNEL_ID) {
            uiState.setHasUpdatedThreadChannel(false);
            uiState.setHasUnreadThread(false);
            threadChannelStore.clearMainChannelList();
            threadChannelStore.setMainThreadChannelAllLoaded(false);
            threadChannelStore.setMainThreadChannelPage(0);
            this.actions.threadChannelActions.fetchThreadMainChannel();
          } else if (mainChannelId) {
            this.actions.logActions.fetchLatestLogs(mainChannelId);
            this.actions.channelMembersActions.debouncedFetchChannelReadSeq(mainChannelId);
          }

          const threadChannelId = threadDetailUiState.contentsChannelId;
          if (uiState.threadOpen && threadChannelId) {
            this.actions.threadChannelActions.openThreadChannel(threadChannelId);
            this.actions.threadLogActions.fetchLatestLogs(threadChannelId);
          }
        },
      ])
    );
  };

  private executeReconnectJob = throttle(
    () => {
      if (this.websocketStore.reconnectStartTime === 0) {
        this.type === WebsocketHandlerType.InterTenant
          ? this.executeInterTenantReconnectJob()
          : this.executeDefaultReconnectJob();
      }
    },
    50,
    { leading: true, trailing: false }
  );

  private executeInterTenantReconnectJob = () => {
    uiState.setIsInterTenantWebsocketOn(false);
    this.changeCurrentChannel();

    if (!this.isSessionOut) {
      this.reconnectInterTenantWebsocket();
    }
  };

  private executeDefaultReconnectJob = () => {
    uiState.setIsWebsocketOn(false);
    if (!this.isSessionOut) {
      if (isMainView()) {
        this.actions.websocketActions.reconnectWebsocket().catch(() => {
          uiState.openModal({
            name: MODAL_NAMES.RECONNECT,
            args: { stopRetry: this.ws.stopRetry, websocketActions: this },
          });
        });
        return;
      }
      uiState.openModal({
        name: MODAL_NAMES.RECONNECT,
        args: { stopRetry: this.ws.stopRetry, websocketActions: this },
      });
    }
  };

  private reconnectInterTenantWebsocket = () => {
    this.interTenantReconnectObserver?.();
    this.interTenantReconnectObserver = reaction(
      () => uiState.isOnline && !this.websocketStore.preventReconnectingDurationMs,
      (isOnLine: boolean) => {
        if (isOnLine) {
          this.startReconnect(() =>
            this.reconnectWebsocket().then(() => {
              this.actions.channelActions.fetchAndCacheInterTenantChannels();
              this.interTenantReconnectObserver();
            })
          );
        } else {
          this.websocketStore.clearReconnect();
        }
      },
      { fireImmediately: true }
    );
  };

  private changeCurrentChannel = async () => {
    if (stores.isSharedContentsChannel) {
      await userStore.fetchAndCache();
      this.actions.channelActions.selectChannel(userStore.lastChannelId);
    }
  };

  private cacheMemberStatusRequest = (body) => {
    return this.type === WebsocketHandlerType.Default
      ? membersStore.cacheMemberStatusRequest(body)
      : interTenantMembersStore.cacheMemberStatusRequest(body);
  };

  public attemptReconnect = (reconnect: () => Promise<void>) => {
    if (this.websocketStore.disableReconnect || this.websocketStore.preventReconnectingDurationMs) {
      return;
    }
    this.websocketStore.setDisableReconnect(true);
    return this.healthCheck()
      .then(this.checkAuth)
      .then(reconnect)
      .then(() => {
        this.websocketStore.clearReconnect();
      }, juxt([() => this.websocketStore.setNextReconnectTime(), () => this.websocketStore.setDisableReconnect(false)]));
  };

  private healthCheck = () => {
    return api.common.healthCheck();
  };

  private checkAuth = () => {
    return userStore.fetchAndCache();
  };

  private parseToTypeDataShape = (obj) => {
    return {
      type: this.getPublishMessageType(obj),
      data: obj.content || obj.contents,
      references: obj.references,
    };
  };

  private getPublishMessageType = ({ action, type }) => {
    return isEmpty(action) ? type : replace(/ /g, '', `${type}${startCase(action)}`);
  };

  private handleParsedMessage = (message) => {
    if (message.type === 'pong') {
      this.clearPingCount();
    }
    this.publishMessage(message);
    this.sendMessageToNative(message);
  };

  @action
  private publishMessage = ({ type, data, version, references }) => {
    switch (type) {
      case 'channelArchiveOn':
        this.channel.handleArchive(data);
        break;
      case 'channelArchiveOff':
        this.channel.handleUnarchive(data);
        break;
      case 'channelCreate':
        this.channel.handleCreate(data as Channel);
        break;
      case 'channelDisplayOff':
        this.channel.handleDisplay(data);
        break;
      case 'channelDisplayOn':
        this.channel.handleDisplay(data);
        break;
      case 'channelUpdate':
        this.channel.handleUpdate(data as Partial<Channel>);
        break;
      case 'channelDelete':
        this.threadChannel.handleChannelDelete(data);
        break;
      case 'channelPreferenceUpdate':
        this.channel.handleUpdatePreference(data);
        break;
      case 'channelTranslateUpdate':
        this.channel.handleUpdateTranslation(data);
        break;
      case 'channelLogCreate':
        this.log.handleCreate(data as Log, references);
        break;
      case 'channelLogDelete':
        this.log.handleDelete(data as Log);
        break;
      case 'channelLogUpdate':
        this.log.handleUpdate(data as Log);
        break;
      case 'memberStatusLocal':
        this.member.handleLocalStatus(data);
        break;
      case 'memberStatusUpdate':
        this.member.handleStatusUpdate(data);
        break;
      case 'memberUpdate':
        this.member.handleUpdate(data);
        break;
      case 'channelAdminRegister':
        this.channel.handleAdminRegister(data);
        break;
      case 'channelAdminRemove':
        this.channel.handleAdminRemove(data);
        break;
      case 'channelManagementUpdate':
        this.channel.handleManagementUpdate(data);
        break;
      case 'channelMemberJoin':
        this.channel.handleMemberJoin(data, version);
        break;
      case 'channelMemberLeave':
        this.channel.handleMemberLeave(data);
        break;
      case 'channelMemberRemove':
        this.channel.handleMemberRemove(data);
        break;
      case 'channelMemberReadSeqUpdate':
        this.channel.handleReadSeqUpdate(data as ChannelMemberReadSeqUpdateData);
        break;
      case 'channelNoticeTopped':
        this.notice.handlePosted(data);
        break;
      case 'channelNoticeUntopped':
        this.notice.handleUnposted(data);
        break;
      case 'channelFavoriteCreate':
        this.favorite.handleCreate(data);
        break;
      case 'channelFavoriteRemove':
        this.favorite.handleRemove(data);
        break;
      case 'channelFavoriteNameUpdate':
        this.favorite.handleNameUpdate(data);
        break;
      case 'channelFavoriteOrderUpdate':
        this.favorite.handleUpdateOrder(data);
        break;
      case 'channelMemberLangUpdate':
        this.member.handleUpdateLang(data);
        break;
      case 'memberPreferenceUpdate':
        this.common.handleUpdateMemberPreference(data);
        break;
      case 'streamPushUpdate':
        this.common.handleUpdateStreamPush(data);
        break;
      case 'toastContinuousDisplay':
        this.common.handleDisplayToastContinuous(data);
        break;
      case 'toast':
        this.common.handleToast(data);
        break;
      case 'serverUpgrade':
        this.common.handleServerUpgrade();
        break;
      case 'webSocketCloseConnectSameSessionKey':
        this.common.handleConnectSameSession();
        break;
      case 'memberWithdraw':
        this.common.handleMemberWithdraw();
        break;
      case 'websocketNotifyDisconnection':
        this.handleWebsocketNotifyDisconnection(data);
        break;
      case 'dialogOpen':
        this.common.handleDialogOpen(data);
        break;
      case 'threadChannelMemberJoin':
        this.threadChannel.handleMemberJoin(data);
        break;
      case 'threadChannelMemberLeave':
        this.threadChannel.handleMemberLeave(data);
        break;
      case 'threadStatusUpdate':
        this.threadChannel.handleThreadStatusUpdate(data);
        break;
      case 'taskCreate':
        this.threadChannel.handleTaskCreate(data);
        break;
      case 'taskDelete':
        this.threadChannel.handleTaskDelete(data);
        break;
      case 'reactionUpdate':
        this.reaction.handleUpdate(data);
        break;
    }
  };

  private handleWebsocketNotifyDisconnection = (data: { duration: number }) => {
    const duration = data.duration * 1000;
    this.websocketStore.setPreventReconnectingDurationMs(duration);
  };

  private sendMessageToNative = (message) => {
    pipe<any, any, any>(this.isNonMemberStatusMessage, map(this.sendToNative(userStore.id)))(message);
  };

  private isNonMemberStatusMessage = (message) => {
    const { type } = message;
    return type !== 'memberStatusUpdate' ? Maybe.Just(message) : Maybe.Nothing();
  };
}

export default WebsocketActions;
